import { getCompanyPaymentProfile } from "@/constants/companyProfiles";
import {
  DEFAULT_QUOTATION_HOURLY_RATE,
  DEFAULT_QUOTATION_INSPECTION_FEE,
} from "@/constants/quotationDefaults";
import {
  DEFAULT_QUOTATION_TEMPLATE_DEFINITION,
  getQuotationScopeCatalogueSelectionFromTemplate,
  type QuotationScopeCatalogueSection,
  type QuotationTemplateDefinition,
} from "@/constants/quotationOptions";
import type { QuotationPdfData, QuotationPaymentStage } from "@/templates/pdf/QuotationTemplate";
import type {
  PublicQuotationSigningSession,
  PublicQuotationSigningQuotation,
  QuotationSigningFormData,
} from "@/services/quotationSigningClient";
import { getQuotationDisplayAmount, getQuotationStructuralFeeTotal } from "@/utils/quotationFees";

type GeneralScopeItemDraft = {
  key: string;
  sectionKey: string;
  sectionTitle: string;
  text: string;
  included: boolean;
};

type QuotationSignatoryProfile = {
  email: string;
  displayName: string;
  company: string;
  position: string;
};

const FALLBACK_SIGNATORY_BY_ENTITY: Record<string, QuotationSignatoryProfile> = {
  "equilibrium engineering pty ltd": {
    email: "wdu@eqeng.com.au",
    displayName: "Wilson Du",
    company: "Equilibrium Engineering Pty Ltd",
    position: "Director",
  },
  "linfinity engineering pty ltd": {
    email: "yl@linfinityeng.com",
    displayName: "Yuan Lin",
    company: "Linfinity Engineering Pty Ltd",
    position: "Director",
  },
};

function toTrimmedString(value: unknown): string {
  return String(value ?? "").trim();
}

function normalizeKey(value: unknown): string {
  return toTrimmedString(value).toLowerCase();
}

function toFeeNumber(value: unknown): number {
  const numeric = typeof value === "number" ? value : Number(String(value ?? "").trim());
  return Number.isFinite(numeric) ? numeric : 0;
}

function normalizePostCode(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(Math.trunc(value)) : "";
  }
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? String(Math.trunc(numeric)) : trimmed;
}

function parseStringArrayValue(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => toTrimmedString(item)).filter(Boolean);
  }

  try {
    const parsed = JSON.parse(String(value));
    return Array.isArray(parsed)
      ? parsed.map((item) => toTrimmedString(item)).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

function parseGeneralScopeItemDrafts(value: unknown): GeneralScopeItemDraft[] {
  if (!value) return [];

  let rawItems: unknown[] = [];
  if (Array.isArray(value)) {
    rawItems = value;
  } else if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      rawItems = Array.isArray(parsed) ? parsed : [];
    } catch {
      rawItems = [];
    }
  }

  return rawItems.flatMap((item, index) => {
    if (!item || typeof item !== "object") {
      return [];
    }

    const record = item as Record<string, unknown>;
    const sectionKey = toTrimmedString(record.sectionKey ?? record.SectionKey);
    const sectionTitle = toTrimmedString(record.sectionTitle ?? record.SectionTitle);

    return [{
      key: toTrimmedString(record.key ?? record.Key) || `${sectionKey || "general"}-${index}`,
      sectionKey: sectionKey || "general",
      sectionTitle: sectionTitle || "Other Scope Details",
      text: toTrimmedString(record.text ?? record.Text),
      included: record.included !== false && record.Included !== false,
    }];
  });
}

function normalizeQuotationPdfDate(value: unknown): string {
  const trimmed = toTrimmedString(value);
  if (!trimmed) {
    return new Date().toISOString().slice(0, 10);
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return trimmed.slice(0, 10);
  }

  return parsed.toISOString().slice(0, 10);
}

function parseQuotationPaymentStages(value: unknown): QuotationPaymentStage[] {
  if (!value) return [];

  let parsed: any = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
  }

  const rawStages = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.stages)
      ? parsed.stages
      : [];

  return rawStages
    .map((stage: any, index: number) => {
      const amount = Number(stage?.amount ?? stage?.Amount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        return null;
      }

      return {
        key: toTrimmedString(stage?.key ?? stage?.Key ?? `payment-stage-${index + 1}`),
        stageKey: toTrimmedString(stage?.stageKey ?? stage?.StageKey ?? "final") || "final",
        componentKey: toTrimmedString(stage?.componentKey ?? stage?.ComponentKey ?? "quotation") || "quotation",
        componentLabel: toTrimmedString(stage?.componentLabel ?? stage?.ComponentLabel ?? "Payment") || "Payment",
        label: toTrimmedString(stage?.label ?? stage?.Label ?? "Payment") || "Payment",
        requestedPercentage: Number(stage?.requestedPercentage ?? stage?.RequestedPercentage ?? stage?.percentage ?? stage?.Percentage ?? 0) || 0,
        percentage: Number(stage?.percentage ?? stage?.Percentage ?? 0) || 0,
        amount,
        reference: toTrimmedString(stage?.reference ?? stage?.Reference),
        description: toTrimmedString(stage?.description ?? stage?.Description) || undefined,
        triggerLabel: toTrimmedString(stage?.triggerLabel ?? stage?.TriggerLabel) || undefined,
      } satisfies QuotationPaymentStage;
    })
    .filter((stage): stage is QuotationPaymentStage => Boolean(stage));
}

function dedupeScopeItems(items: string[]) {
  const seen = new Set<string>();
  return items
    .map((item) => item.replace(/\s+/g, " ").trim())
    .filter((item) => {
      const key = item.toLowerCase();
      if (!item || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function buildScopeSectionFromSelection(
  discipline: "structural" | "civil",
  groupKey: string,
  groupLabel: string,
  items: string[],
): QuotationScopeCatalogueSection | null {
  const bullets = dedupeScopeItems(items);
  if (bullets.length === 0) {
    return null;
  }

  const normalizedKey = normalizeKey(groupKey) || normalizeKey(groupLabel) || `${discipline}-custom`;
  const title = groupLabel || groupKey || (discipline === "structural" ? "Detailed Structural Scope" : "Detailed Civil Scope");

  return {
    key: `selected-${discipline}-${normalizedKey}`,
    title,
    tint: discipline,
    bullets,
  };
}

function buildGeneralScopeSectionsFromDrafts(
  drafts: GeneralScopeItemDraft[],
  fallbackSections: QuotationScopeCatalogueSection[],
): QuotationScopeCatalogueSection[] {
  const fallbackByKey = new Map(fallbackSections.map((section) => [section.key, section]));
  const grouped = new Map<string, QuotationScopeCatalogueSection>();

  drafts.forEach((draft, index) => {
    const text = toTrimmedString(draft.text).replace(/\s+/g, " ").trim();
    if (!draft.included || !text) {
      return;
    }

    const fallbackSection = fallbackByKey.get(draft.sectionKey) || null;
    const key = draft.sectionKey || fallbackSection?.key || `general-${index + 1}`;
    const title = draft.sectionTitle || fallbackSection?.title || "Other Scope Details";
    const existing = grouped.get(key);

    if (existing) {
      existing.bullets.push(text);
      return;
    }

    grouped.set(key, {
      key,
      title,
      tint: "general",
      bullets: [text],
    });
  });

  const orderedFallbackSections = fallbackSections
    .map((section) => {
      const group = grouped.get(section.key);
      return group ? { ...group, bullets: dedupeScopeItems(group.bullets) } : null;
    })
    .filter((section): section is QuotationScopeCatalogueSection => Boolean(section) && section.bullets.length > 0);

  const customSections = [...grouped.entries()]
    .filter(([key]) => !fallbackByKey.has(key))
    .map(([, section]) => ({ ...section, bullets: dedupeScopeItems(section.bullets) }))
    .filter((section) => section.bullets.length > 0);

  return [...orderedFallbackSections, ...customSections];
}

function mergeTemplateDefinition(
  templateDefinition: Partial<QuotationTemplateDefinition> | null | undefined,
): QuotationTemplateDefinition {
  const fallback = DEFAULT_QUOTATION_TEMPLATE_DEFINITION;
  return {
    ...fallback,
    ...templateDefinition,
    projectScopeOptions: Array.isArray(templateDefinition?.projectScopeOptions) ? templateDefinition.projectScopeOptions : fallback.projectScopeOptions,
    projectTypeOptions: Array.isArray(templateDefinition?.projectTypeOptions) ? templateDefinition.projectTypeOptions : fallback.projectTypeOptions,
    scopeSections: Array.isArray(templateDefinition?.scopeSections) ? templateDefinition.scopeSections : fallback.scopeSections,
    structuralTypeScopeMap: templateDefinition?.structuralTypeScopeMap || fallback.structuralTypeScopeMap,
    civilTypeScopeMap: templateDefinition?.civilTypeScopeMap || fallback.civilTypeScopeMap,
    exclusionOptions: Array.isArray(templateDefinition?.exclusionOptions) ? templateDefinition.exclusionOptions : fallback.exclusionOptions,
    termsConditions: Array.isArray(templateDefinition?.termsConditions) ? templateDefinition.termsConditions : fallback.termsConditions,
  };
}

function resolveSignatoryProfile(quotation: PublicQuotationSigningQuotation | null | undefined): QuotationSignatoryProfile {
  const entity = toTrimmedString(quotation?.Entity);
  const normalizedEntity = entity.toLowerCase();
  const fallback = FALLBACK_SIGNATORY_BY_ENTITY[normalizedEntity] || {
    email: "",
    displayName: "",
    company: entity || "Linfinity Engineering Pty Ltd",
    position: "Director",
  };
  const preferredEmail = toTrimmedString(quotation?.CertifiedBy || quotation?.BroughtBy).toLowerCase();

  return {
    email: preferredEmail || fallback.email,
    displayName: fallback.displayName,
    company: fallback.company,
    position: fallback.position,
  };
}

function buildScopeSections(
  quotation: PublicQuotationSigningQuotation,
  templateDefinition: QuotationTemplateDefinition,
): QuotationScopeCatalogueSection[] {
  const inferredSections = getQuotationScopeCatalogueSelectionFromTemplate(templateDefinition, {
    projectScope: quotation.ProjectScope || "",
    projectType: quotation.ProjectType || "",
    projectDescription: quotation.ProjectDescription || "",
  });
  const fallbackSections = {
    structural: inferredSections.filter((section) => section.tint === "structural"),
    civil: inferredSections.filter((section) => section.tint === "civil"),
    general: inferredSections.filter((section) => section.tint === "general"),
  };

  const storedStructuralSection = buildScopeSectionFromSelection(
    "structural",
    toTrimmedString(quotation.StructuralScopeGroupKey),
    toTrimmedString(quotation.StructuralScopeGroupLabel),
    parseStringArrayValue(quotation.StructuralScopeItemsJson),
  );
  const storedCivilSection = buildScopeSectionFromSelection(
    "civil",
    toTrimmedString(quotation.CivilScopeGroupKey),
    toTrimmedString(quotation.CivilScopeGroupLabel),
    parseStringArrayValue(quotation.CivilScopeItemsJson),
  );
  const includeGeneralScope = quotation.IncludeGeneralScope === undefined || quotation.IncludeGeneralScope === null || quotation.IncludeGeneralScope === ""
    ? false
    : !["false", "0", "no", "off"].includes(normalizeKey(quotation.IncludeGeneralScope));
  const storedGeneralScopeDrafts = parseGeneralScopeItemDrafts(quotation.GeneralScopeItemsJson);

  return [
    ...(storedStructuralSection ? [storedStructuralSection] : fallbackSections.structural),
    ...(storedCivilSection ? [storedCivilSection] : fallbackSections.civil),
    ...(includeGeneralScope
      ? (storedGeneralScopeDrafts.length > 0
          ? buildGeneralScopeSectionsFromDrafts(storedGeneralScopeDrafts, fallbackSections.general)
          : fallbackSections.general)
      : []),
  ].filter((section): section is QuotationScopeCatalogueSection => Boolean(section));
}

export function buildSignedQuotationFileName(quotationNumber: string | null | undefined): string {
  const safe = toTrimmedString(quotationNumber || "quotation")
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return `${safe || "quotation"}-signed.pdf`;
}

export function buildQuotationSigningPdfData(
  session: PublicQuotationSigningSession,
  form: QuotationSigningFormData,
): QuotationPdfData {
  if (!session.quotation) {
    throw new Error("Quotation details are missing from this signing session.");
  }

  const quotation = session.quotation;
  const templateDefinition = mergeTemplateDefinition(session.templateDefinition);
  const companyProfile = getCompanyPaymentProfile(quotation.Entity);
  const signatoryProfile = resolveSignatoryProfile(quotation);
  const structuralFee = getQuotationStructuralFeeTotal(quotation);
  const civilFee = toFeeNumber(quotation.CivilFee);
  const soilReportFee = toFeeNumber(quotation.SoilReportFee);
  const storedTotal = getQuotationDisplayAmount(quotation);
  const paymentSchedule = [
    ...parseQuotationPaymentStages(quotation.StructuralPaymentScheduleJson),
    ...parseQuotationPaymentStages(quotation.CivilPaymentScheduleJson),
  ];

  return {
    quotationNumber: toTrimmedString(session.quotationNumber || quotation.QuotationNumber || quotation.QuotationId) || "DRAFT",
    issueDate: normalizeQuotationPdfDate(quotation.Date || quotation.IssueDate),
    drawingReceivedDate: normalizeQuotationPdfDate(quotation.Date || quotation.IssueDate),
    entity: companyProfile.entity,
    clientName: toTrimmedString(form.clientName) || "Client name",
    companyName: toTrimmedString(form.clientCompany),
    abn: toTrimmedString(form.clientAbn),
    clientAddress: toTrimmedString(form.clientAddress),
    clientMobile: toTrimmedString(form.clientPhone),
    clientEmail: toTrimmedString(form.clientEmail),
    projectScope: toTrimmedString(quotation.ProjectScope),
    projectType: toTrimmedString(quotation.ProjectType),
    projectDescription: toTrimmedString(quotation.ProjectDescription),
    projectStreetAddress: toTrimmedString(quotation.Address),
    projectSuburb: toTrimmedString(quotation.Suburb),
    postCode: normalizePostCode(quotation.PostCode),
    council: toTrimmedString(quotation.Council),
    senderDisplayName: toTrimmedString(quotation.SenderDisplayName) || companyProfile.senderDisplayName,
    senderAddress: toTrimmedString(quotation.SenderAddress) || companyProfile.senderAddress,
    senderMobile: toTrimmedString(quotation.SenderMobile) || companyProfile.senderMobile,
    senderEmail: toTrimmedString(quotation.SenderEmail) || companyProfile.senderEmail,
    senderPayId: toTrimmedString(quotation.SenderPayId) || companyProfile.senderPayId || "",
    senderBsb: toTrimmedString(quotation.SenderBsb) || companyProfile.senderBsb,
    senderAccountNumber: toTrimmedString(quotation.SenderAccountNumber) || companyProfile.senderAccountNumber,
    senderAccountName: toTrimmedString(quotation.SenderAccountName) || companyProfile.senderAccountName,
    signatoryEmail: signatoryProfile.email,
    signatoryName: signatoryProfile.displayName,
    signatoryCompany: signatoryProfile.company,
    signatoryPosition: signatoryProfile.position,
    exclusions: parseStringArrayValue(quotation.ExclusionsJson),
    termsConditions: templateDefinition.termsConditions || [],
    structuralScopeFootnote: parseStringArrayValue(quotation.StructuralScopeItemsJson).length > 0 ? toTrimmedString(quotation.ScopeFootnoteS) : "",
    civilScopeFootnote: parseStringArrayValue(quotation.CivilScopeItemsJson).length > 0 ? toTrimmedString(quotation.ScopeFootnoteC) : "",
    structuralFee,
    civilFee,
    soilReportFee,
    hourlyRate: toFeeNumber(quotation.HourlyRate) || DEFAULT_QUOTATION_HOURLY_RATE,
    inspectionFee: toFeeNumber(quotation.InspectionFee) || DEFAULT_QUOTATION_INSPECTION_FEE,
    quoteValidityDays: Number(quotation.QuoteValidityDays ?? 30),
    projectManagementFee: 0,
    quotedFeeTotal: structuralFee + civilFee,
    totalQuote: storedTotal > 0 ? storedTotal : structuralFee + civilFee + soilReportFee,
    scopeSections: buildScopeSections(quotation, templateDefinition),
    paymentSchedule,
  };
}
